import { useEffect, useState } from "react";
import { Instagram, ExternalLink, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { normalizeImageUrl } from "@/lib/api";
import { sizedImage } from "@/lib/imageUtils";
import { safeJsonLd } from "@/lib/safeJsonLd";

const formatDate = (iso, lang) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(lang === "en" ? "en-US" : "it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const NewsSection = () => {
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);
  const lang = i18n.language?.startsWith("en") ? "en" : "it";

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/content/news");
        const c = res.data?.content || res.data;
        setData(c);
      } catch (_) {
        setData(null);
      }
    };
    load();
  }, []);

  const allItems = (data?.items || []).filter((it) => it && (it.title_it || it.title_en));
  // Most recent first by published_date
  const items = [...allItems].sort((a, b) => {
    const da = a.published_date ? new Date(a.published_date).getTime() : 0;
    const dbb = b.published_date ? new Date(b.published_date).getTime() : 0;
    return dbb - da;
  });

  if (!data || data.is_visible === false || items.length === 0) return null;

  const title = lang === "en" ? data.title_en || data.title_it : data.title_it;
  const subtitle = lang === "en" ? data.subtitle_en || data.subtitle_it : data.subtitle_it;
  const handle = data.instagram_handle;

  // JSON-LD ItemList of NewsArticle for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": items.map((it, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "item": {
        "@type": "NewsArticle",
        "headline": it.title_it || it.title_en,
        "image": it.image ? [normalizeImageUrl(it.image)] : undefined,
        "datePublished": it.published_date || undefined,
        "articleBody": it.content_it || it.content_en || "",
        "url": it.instagram_url || undefined,
        "publisher": {
          "@type": "Organization",
          "name": "Agriturismo Sadira",
        },
      },
    })),
  };

  return (
    <section data-reveal className="py-16 md:py-24 bg-[#f8f5ee]" data-testid="home-news-section" id="notizie">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd) }}
      />
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10 md:mb-14">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#7e823a] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="home-news-title"
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-base md:text-lg text-[#5a5a5a] max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
          {handle && (
            <a
              href={`https://www.instagram.com/${handle.replace(/^@/, "")}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-[#7e823a] hover:text-[#5a5e25] transition-colors"
              data-testid="home-news-instagram-handle"
            >
              <Instagram className="w-4 h-4" />
              @{handle.replace(/^@/, "")}
            </a>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((it, idx) => {
            const itTitle = lang === "en" ? it.title_en || it.title_it : it.title_it;
            const itContent = lang === "en"
              ? it.content_en || it.content_it
              : it.content_it;
            const itExcerpt = lang === "en"
              ? it.excerpt_en || it.excerpt_it
              : it.excerpt_it;
            const dateLabel = formatDate(it.published_date, lang);
            const img = it.image ? sizedImage(normalizeImageUrl(it.image), 800) : null;
            return (
              <article
                key={idx}
                itemScope
                itemType="https://schema.org/NewsArticle"
                className="group bg-white rounded-2xl overflow-hidden border border-[#E5E0D8] hover:shadow-xl transition-all duration-300"
                data-testid={`home-news-item-${idx}`}
              >
                {img && (
                  <div className="aspect-[4/3] overflow-hidden bg-secondary/30">
                    <img
                      src={img}
                      alt={itTitle}
                      itemProp="image"
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-5 md:p-6 space-y-3">
                  {dateLabel && (
                    <div className="flex items-center gap-2 text-xs text-[#8C8C8C]">
                      <Calendar className="w-3.5 h-3.5" />
                      <time
                        itemProp="datePublished"
                        dateTime={it.published_date}
                      >
                        {dateLabel}
                      </time>
                    </div>
                  )}
                  <h3
                    itemProp="headline"
                    className="text-lg font-semibold text-[#2C2C2C] leading-snug"
                  >
                    {itTitle}
                  </h3>
                  {(itExcerpt || itContent) && (
                    <p
                      itemProp="description"
                      className="text-sm text-[#5a5a5a] leading-relaxed line-clamp-4 whitespace-pre-line"
                    >
                      {itExcerpt || itContent}
                    </p>
                  )}
                  {/* Hidden full content for SEO crawlers */}
                  {itContent && (
                    <div className="sr-only" itemProp="articleBody">
                      {itContent}
                    </div>
                  )}
                  {it.instagram_url && (
                    <a
                      href={it.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#7e823a] hover:text-[#5a5e25] transition-colors pt-2"
                      data-testid={`home-news-instagram-link-${idx}`}
                    >
                      <Instagram className="w-4 h-4" />
                      {lang === "en" ? "View on Instagram" : "Vedi su Instagram"}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
